// @ts-nocheck
/* eslint-disable @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-assignment, @typescript-eslint/strict-boolean-expressions */

import { TooltipProps } from 'recharts';
import {
  NameType,
  ValueType,
} from 'recharts/types/component/DefaultTooltipContent';
import { useTheme } from '@mui/material/styles';
import { Stack, Typography } from '@mui/material';
import {
  CompassChartData,
  CompassSymbolData,
  EquityFieldKey,
  StrategyCompassXYZAxisReadableMap,
} from '../../../types';
import { displayValue, generateTooltipText } from '../../../util/compass';
import { CompassParams } from '../../../types/compass';
import { useRecoilValue } from 'recoil';
import { earningsCalendarBySymState } from '../../../states';
import { ET, dayjs, formatAsPercentage } from '../../../util';
import { EARNINGS_PERIOD_LABEL } from '../../../config/home';
import { useCompassCtx } from './useCompassCtx';
import { useEffect } from 'react';

type StrategyCompassTooltipProps = {
  chartData: CompassChartData[];
  symDataMap: Map<string, CompassSymbolData>;
  compassParams: CompassParams;
} & TooltipProps<ValueType, NameType>;

export const StrategyCompassTooltip = ({
  active,
  payload,
  chartData,
  symDataMap,
  compassParams,
}: StrategyCompassTooltipProps) => {
  const earningsCalendar = useRecoilValue(earningsCalendarBySymState);
  const ctx = useCompassCtx();
  const { setHoveredSymbol, hoveredSymData, setHoveredSymData } = ctx;
  const compassHistoryLookback = ctx.config.historyLookback;

  const theme = useTheme();
  const { xAxis, yAxis, zAxis, zAxisDataKey } = compassParams;
  useEffect(() => {
    const symData = active ? payload?.[0]?.payload : undefined;
    if (symData) {
      setHoveredSymbol(symData?.sym);
      setHoveredSymData(symData);
    }
  }, [active, payload, setHoveredSymbol, setHoveredSymData]);

  if (active && hoveredSymData) {
    const data = hoveredSymData as CompassChartData;
    const sym = data.sym;
    const symChartData = chartData.find((d) => d.sym === sym)!;
    const symData = symDataMap.get(sym)!;
    const earningsEntry = earningsCalendar?.get(sym);

    return (
      <Stack
        sx={{
          backgroundColor: theme.palette.background.paper,
          color: theme.palette.text.primary,
          padding: '10px',
          borderRadius: '4px',
        }}
      >
        <Typography
          sx={{
            textAlign: 'center',
            textDecoration: 'underline',
            fontWeight: 'bold',
            fontSize: '15px',
          }}
        >
          {sym}
          {compassHistoryLookback > 0 && data.date?.format(' - ddd MMM D')}
        </Typography>
        <Stack sx={{ '*': { fontSize: '13px' } }} direction="row">
          <Stack sx={{ marginTop: '3px' }} gap={'1px'}>
            <Typography>
              {StrategyCompassXYZAxisReadableMap.get(xAxis)}:{' '}
              <b>{displayValue(data.unroundedX, xAxis)}</b>
            </Typography>
            <Typography>
              {StrategyCompassXYZAxisReadableMap.get(yAxis)}:{' '}
              <b>{displayValue(data.unroundedY, yAxis)}</b>
            </Typography>
            {zAxisDataKey != null && (
              <>
                <Typography>
                  {StrategyCompassXYZAxisReadableMap.get(zAxis!)}:{' '}
                  <b>{displayValue(data.unroundedZ, zAxis!)}</b>
                </Typography>
                <Typography
                  sx={{ fontSize: '10px', color: theme.palette.text.secondary }}
                >
                  (the z axis is displayed as the size of the dot)
                </Typography>
              </>
            )}
            <Typography>
              Price: <b>${(data?.price ?? symData?.price)?.toFixed(2)}</b>
              &nbsp;&nbsp;Call wall: <b>{data?.cws ?? symData?.equity?.cws}</b>
              &nbsp;&nbsp;Put wall: <b>{data?.pws ?? symData?.equity?.pws}</b>
            </Typography>
            <Typography>
              1 M IV:{' '}
              <b>
                {formatAsPercentage(
                  data?.atm_iv30 ?? symData?.equity?.[EquityFieldKey.iv30],
                )}
              </b>
              &nbsp;&nbsp;1 M RV:{' '}
              <b>
                {formatAsPercentage(
                  data?.rv30 ?? symData?.equity?.[EquityFieldKey.rv30],
                )}
              </b>
            </Typography>
            {earningsCalendar === null && (
              <Typography
                sx={{
                  fontSize: '13px',
                  color: theme.palette.primary.main,
                }}
              >
                Error: Earnings dates unavailable
              </Typography>
            )}
            {earningsEntry != null && (
              <Typography
                sx={{
                  fontSize: '13px',
                  color: theme.palette.primary.main,
                }}
              >
                Earnings:{' '}
                {EARNINGS_PERIOD_LABEL.get(earningsEntry.period) ?? ''}{' '}
                {dayjs.tz(earningsEntry.day, ET).format('dddd MM/DD')}
              </Typography>
            )}
          </Stack>
        </Stack>

        <>
          <hr style={{ marginTop: '8px', marginBottom: '8px' }} />
          <Stack sx={{ maxWidth: '250px', gap: '5px' }}>
            {symChartData &&
              generateTooltipText(symChartData, symData, compassParams)}
          </Stack>
        </>
      </Stack>
    );
  }
  return null;
};
